import { useEffect } from 'react'
import { View } from 'react-native'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { format as formatDateFns, isValid as isValidDate } from 'date-fns'
import { BirthdateValues } from '@ca/common'
import FormInput from '@ui/forms/Input/FormInput'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import InputAction from '@ui/forms/Input/InputAction'
import { personalStepSchema } from '../stepSchemas'
import useCreateCustomerStore from '../useCreateCustomerStore'
import { getAge } from 'features/order/hooks/values/OrderValues'

interface Props {
  submitRef: React.MutableRefObject<() => void>
  onSuccess: () => void
  setIsPending?: (v: boolean) => void
}

const formatBirthDate = (date: Date) => {
  if (!isValidDate(date)) {
    return ''
  }
  return formatDateFns(date, 'dd.MM.yyyy')
}

export default function PersonalStep({ submitRef, onSuccess, setIsPending }: Props) {
  const { t } = useTranslation(['user'])
  const { data, updateData } = useCreateCustomerStore()

  const form = useForm({
    resolver: zodResolver(personalStepSchema),
    defaultValues: {
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      birthday: data.birthday ?? BirthdateValues.default,
    },
  })

  const birthdayValue = useWatch({
    control: form.control,
    name: 'birthday',
  })

  const age = getAge(birthdayValue instanceof Date ? birthdayValue : null)
  const ageLabel =
    age !== null && age >= 0 ? t('user:yearsOld', { count: age }) : undefined

  useEffect(() => {
    submitRef.current = form.handleSubmit(
      (values) => {
        updateData(values)
        onSuccess()
      },
      () => setIsPending?.(false)
    )
  })

  return (
    <FormProvider {...form}>
      <View className="gap-8">
        <View className="flex-row gap-8">
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="first_name"
              label={t('user:firstName')}
              labelVariant="top"
              required
              next="last_name"
              autoCorrect={false}
              autoCapitalize="words"
            />
          </View>
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="last_name"
              label={t('user:lastName')}
              labelVariant="top"
              required
              autoCorrect={false}
              autoCapitalize="words"
            />
          </View>
        </View>
        <FormInputDate
          control={form.control}
          name="birthday"
          label={t('user:birthDate')}
          labelVariant="top"
          required
          min={BirthdateValues.min}
          max={new Date()}
          defaultDate={BirthdateValues.default}
          formatDate={formatBirthDate}
          textEnd={ageLabel}
          enableEdit={false}
          action={<InputAction icon="calendar-event" />}
        />
      </View>
    </FormProvider>
  )
}
